import {
	ReactElement,
	RefObject,
	useCallback,
	useContext,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState
} from "react";
import {
	Dialog,
	doOverlap,
	LassoContainer,
	LassoContainerContext,
	lassoModeToScopeKind,
	NoResults,
	SearchInputForm,
	SearchPlaceholder,
	Toolbar
} from "client/Components";
import {
	AlertManager,
	createLocalDataSource,
	ItemSelectionKind,
	SelectableItemOptions,
	useAlerts,
	useDataInputDialog,
	useResizeObserver,
	useSelection,
	useStableImperativeCallback
} from "client/Hooks";
import { localize } from "client_legacy/ndaw/culture";
import {
	getSearchIntegrationFlows,
	SearchIntegrationFlowOptionModel
} from "client/Api/SearchSettings/searchSettings";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import {
	EMPTY_ARRAY,
	generateTrackingId,
	isNotNilOrWhiteSpace
} from "client/Misc/utils";
import { Rect } from "client/Misc/commonTypes";
import { contains } from "client/Data/searching";
import { DataSourceSortableState, DataSourceSortDirection } from "client/Data/sorting";
import { newGuid } from "client_legacy/ndaw/guid";
import { SizeInformation } from "client_legacy/ndaw/sizing";
import { IconCard } from "client/Features/Apps/ChooseAppIconDialog/IconCard";
import styles from "./AddIntegrationDialog.module.less";

export type AddIntegrationDialogProps = {
	addedIntegrationIds: readonly number[];
	onAdd: (integrations: readonly SearchIntegrationFlowOptionModel[]) => void;
	onClose: () => void;
};

export const AddIntegrationDialog = (props: AddIntegrationDialogProps): ReactElement => {
	const {
		addedIntegrationIds,
		onAdd,
		onClose
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [integrations, setIntegrations] = useState<readonly SearchIntegrationFlowOptionModel[]>(EMPTY_ARRAY);
	const [searchTerm, setSearchTerm] = useState<VersionedValue<string>>(
		() => ({ value: "", version: newGuid() })
	);
	const [selectedIntegrations, setSelectedIntegrations] = useState<readonly SearchIntegrationFlowOptionModel[]>(EMPTY_ARRAY);

	const alertManager: AlertManager = useAlerts();

	const availableIntegrations: readonly SearchIntegrationFlowOptionModel[] = useMemo(
		(): readonly SearchIntegrationFlowOptionModel[] => integrations
			.filter(
				x => !addedIntegrationIds.includes(x.integrationId)
			),
		[addedIntegrationIds, integrations]
	);

	const {
		currentItems: itemsToShow
	} = useIntegrationsDataSource(
		availableIntegrations,
		{
			searchTerm: searchTerm.value,
			sorting: SORT_BY_NAME
		}
	);

	const {
		multiSelection,
		selectionFor
	} = useSelection(
		itemsToShow,
		"integrationId",
		{
			isMultiSelect: true,
			selectedItems: selectedIntegrations,
			setSelectedItems: setSelectedIntegrations,
			hasMemorization: true,
			allItems: availableIntegrations
		}
	);

	const addHandler = useCallback(
		(): void => onAdd(selectedIntegrations),
		[onAdd, selectedIntegrations]
	);

	const stableOnClose: () => void = useStableImperativeCallback(onClose);

	useEffect(
		(): (() => void) => {
			async function fetchData(signal: AbortSignal): Promise<void> {
				setIsBusy(true);

				try {
					setIntegrations(
						await getSearchIntegrationFlows(signal)
					);

					setIsBusy(false);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
					stableOnClose();
				}
			}

			const cancellation = new AbortController();
			fetchData(cancellation.signal);

			return () => cancellation.abort();
		},
		[alertManager, stableOnClose]
	);

	const renderItems = (): ReactElement => {
		if (itemsToShow.length > 0) {
			return (
				<ul className={styles.list}>
					{itemsToShow.map(
						x => (
							<li key={x.integrationId}>
								<IntegrationCard
									integration={x}
									selectable={selectionFor(x)}
								/>
							</li>
						)
					)}
				</ul>
			);
		}

		if (isNotNilOrWhiteSpace(searchTerm.value)) {
			return (
				<NoResults
					icon="magnifying-glass"
					title={localize("_NoSearchResults")}
					message={localize("_PleaseTrySearchingWithAnotherTerm")}
					height="fill"
					trackingId="no-search-results"
				/>
			);
		}

		return (
			<NoResults
				icon="grid-2"
				title={localize("_NoIntegrationsToAdd")}
				message={localize("_ThereAreNoIntegrationsAvailableToAdd")}
				height="fill"
				trackingId="no-integrations"
			/>
		);
	};

	return (
		<Dialog
			variant="left-slider"
			hasBusyIndicator={isBusy}
			onClose={onClose}
			trackingId="add-search-integration-dialog"
		>
			<Dialog.Header
				title={localize("_AddIntegration")}
				onClose={onClose}
				hasBorder
			/>
			<Dialog.Body>
				<div className={styles.search}>
					<SearchInputForm
						value={searchTerm}
						onChange={(value, version) => setSearchTerm({ value, version })}
						height="large"
						icon="filter"
						placeholder={(
							<SearchPlaceholder
								kind="filter"
								properties={[localize("_Name")]}
							/>
						)}
						trackingId="search"
					/>
				</div>
				<div className={styles.listContainer}>
					{itemsToShow.length > 1
						? (
							<LassoContainer
								onLassoStart={lm => multiSelection.startSelection(lassoModeToScopeKind[lm])}
								onLassoStop={() => multiSelection.finishSelection()}
							>
								{renderItems()}
							</LassoContainer>
						)
						: renderItems()
					}
				</div>
			</Dialog.Body>
			<Dialog.Footer hasBorder>
				<Toolbar>
					<Toolbar.Button
						kind="primary"
						text={localize("_Add")}
						isDisabled={selectedIntegrations.length === 0}
						onClick={addHandler}
						trackingId="add"
					/>
					<Toolbar.Button
						kind="secondary"
						text={localize("_Cancel")}
						onClick={onClose}
						trackingId="cancel"
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};
AddIntegrationDialog.displayName = "AddIntegrationDialog";

export type AddIntegrationDialogData = Omit<
	AddIntegrationDialogProps,
	"onAdd" | "onClose"
>;

export function useAddIntegrationDialog(): [
	element: ReactElement | null,
	open: (data: AddIntegrationDialogData) => Promise<TryResult<readonly SearchIntegrationFlowOptionModel[]>>
] {
	return useDataInputDialog<AddIntegrationDialogData, readonly SearchIntegrationFlowOptionModel[]>(
		(data, onClose, onAdd) => (
			<AddIntegrationDialog
				{...data}
				onClose={onClose}
				onAdd={onAdd}
			/>
		)
	);
}

const useIntegrationsDataSource = createLocalDataSource<SearchIntegrationFlowOptionModel>()
	.setConfiguration({
		search: {
			doesMatchItem: (integration, searchTerm) => contains(integration.name, searchTerm)
		}
	})
	.getHook();

const SORT_BY_NAME: DataSourceSortableState<SearchIntegrationFlowOptionModel> = {
	direction: DataSourceSortDirection.Ascending,
	propertyName: "name"
};

type IntegrationCardProps = {
	integration: SearchIntegrationFlowOptionModel;
	selectable: SelectableItemOptions;
};

const IntegrationCard = (props: IntegrationCardProps): ReactElement => {
	const {
		integration,
		selectable
	} = props;

	const [doesOverlapWithLasso, setDoesOverlapWithLasso] = useState<boolean>(false);
	const [itemRect, setItemRect] = useState<Rect>({
		left: 0,
		top: 0,
		right: 0,
		bottom: 0
	});

	const itemRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);
	const rowSize: SizeInformation = useResizeObserver(itemRef);

	let isSelected: boolean;
	let onSelect: ((selectionKind: ItemSelectionKind) => void) | undefined;

	if (selectable === false) {
		isSelected = false;
		onSelect = undefined;
	} else {
		isSelected = selectable.isSelected;
		onSelect = selectable.onSelect;
	}

	const {
		color,
		imageDisplayInfo,
		integrationId,
		name
	} = integration;

	const {
		hasLassoContainer,
		lassoRect,
		containerRect
	} = useContext(LassoContainerContext);

	useLayoutEffect(
		() => {
			if (!hasLassoContainer) {
				return;
			}

			if (itemRef.current) {
				const {
					left,
					top,
					right,
					bottom
				} = itemRef
					.current
					.getBoundingClientRect();

				const xOffset: number = containerRect.left;
				const yOffset: number = containerRect.top;

				setItemRect({
					left: left - xOffset,
					top: top - yOffset,
					right: right - xOffset,
					bottom: bottom - yOffset
				});
			}
		},
		[
			containerRect.left,
			containerRect.top,
			hasLassoContainer,
			rowSize.width,
			rowSize.height
		]
	);

	useEffect(
		() => {
			if (!hasLassoContainer || !lassoRect || !itemRect) {
				setDoesOverlapWithLasso(false);
			} else {
				setDoesOverlapWithLasso(doOverlap(lassoRect, itemRect));
			}
		},
		[
			hasLassoContainer,
			lassoRect,
			itemRect
		]
	);

	const hasLassoRect: boolean = hasLassoContainer && Boolean(lassoRect);

	useEffect(
		() => {
			if (onSelect && hasLassoRect) {
				onSelect(
					doesOverlapWithLasso
						? ItemSelectionKind.AddToScope
						: ItemSelectionKind.RemoveFromScope
				);
			}
		},
		[
			doesOverlapWithLasso,
			hasLassoRect,
			onSelect
		]
	);

	return (
		<div ref={itemRef}>
			<IconCard
				name={name}
				icon={imageDisplayInfo}
				iconBackgroundColor={color}
				isSelected={isSelected}
				onSelect={() => onSelect?.(ItemSelectionKind.Append)}
				trackingId={generateTrackingId("integration-item", `|${integrationId}`)}
			/>
		</div>
	);
};
IntegrationCard.displayName = "IntegrationCard";
